// src/features/services/media-v2/components/MusicProjects.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Music production project library — renders the saved-project library and
// supports save (export per project as JSON) + import (paste / upload JSON →
// persist via the BFF). PAC Glass design system, data-freshness aware.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  useMusicProjects,
  useMusicProjectCreate,
  useMusicProjectImport,
} from '../hooks/useMediaV2';
import type { MusicProject, ImportProjectPayload } from '../api/mediaV2Api';

// ── Data-freshness badge (FE/UIX directive) ─────────────────────────────────
type Freshness = 'live' | 'cached' | 'stale' | 'offline';

function freshnessLabel(f: Freshness): string {
  return f === 'live' ? 'Live' : f === 'cached' ? 'Cached' : f === 'stale' ? 'Stale' : 'Offline';
}

function FreshnessBadge({ state }: { state: Freshness }) {
  const tone =
    state === 'live'   ? 'bg-green-900/40 text-green-400'  :
    state === 'cached' ? 'bg-cyan-900/40 text-cyan-400'    :
    state === 'stale'  ? 'bg-amber-900/40 text-amber-400'  :
                         'bg-neutral-800 text-neutral-500';
  return (
    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${tone}`}>
      {freshnessLabel(state)}
    </span>
  );
}

// ── Export helper — download a project as a JSON snapshot ────────────────────
function downloadJson(filename: string, data: unknown) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ── Map an arbitrary parsed object to a safe import payload ──────────────────
function toImportPayload(raw: unknown): ImportProjectPayload | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const name = String(o.project_name ?? o.name ?? '').trim();
  if (!name) return null;
  return {
    project_name: name,
    scope_key:    o.scope_key ? String(o.scope_key) : undefined,
    project_path: o.project_path ? String(o.project_path) : undefined,
    audio_url:    o.audio_url ? String(o.audio_url) : undefined,
    audio_bpm:    o.audio_bpm != null && !Number.isNaN(Number(o.audio_bpm)) ? Number(o.audio_bpm) : undefined,
    url_youtube:  o.url_youtube ? String(o.url_youtube) : undefined,
  };
}

export default function MusicProjects() {
  const query = useMusicProjects();
  const { data, isLoading, isFetching, isError, error, dataUpdatedAt } = query;
  const { create, streaming, clearStream } = useMusicProjectCreate();
  const importMutation = useMusicProjectImport();

  const [projectName, setProjectName] = useState('');
  const [ytUrl, setYtUrl] = useState('');
  const [importText, setImportText] = useState('');
  const [importError, setImportError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const freshness: Freshness = useMemo(() => {
    if (isError) return data ? 'stale' : 'offline';
    if (isFetching) return 'live';
    if (dataUpdatedAt && Date.now() - dataUpdatedAt < 60_000) return 'live';
    return 'cached';
  }, [isError, isFetching, dataUpdatedAt, data]);

  const projects: MusicProject[] = data?.items ?? [];

  const handleCreate = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const name = projectName.trim();
      if (!name) return;
      create.mutate({ project_name: name, url_youtube: ytUrl.trim() || undefined });
    },
    [projectName, ytUrl, create],
  );

  const runImport = useCallback(
    (rawText: string) => {
      setImportError(null);
      let parsed: unknown;
      try {
        parsed = JSON.parse(rawText);
      } catch {
        setImportError('Invalid JSON.');
        return;
      }
      // Accept a single project object or an array of them.
      const list = Array.isArray(parsed) ? parsed : [parsed];
      const payloads = list.map(toImportPayload).filter(Boolean) as ImportProjectPayload[];
      if (payloads.length === 0) {
        setImportError('No importable project found (missing project_name).');
        return;
      }
      payloads.forEach((p) => importMutation.mutate(p));
      setImportText('');
    },
    [importMutation],
  );

  const handleImportText = useCallback(() => {
    if (importText.trim()) runImport(importText);
  }, [importText, runImport]);

  const handleFile = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => runImport(String(reader.result ?? ''));
      reader.onerror = () => setImportError('Failed to read file.');
      reader.readAsText(file);
      e.target.value = '';
    },
    [runImport],
  );

  const exportProject = useCallback((proj: MusicProject) => {
    downloadJson(`${proj.project_name || proj.scope_key}.json`, proj);
  }, []);

  const exportAll = useCallback(() => {
    if (projects.length === 0) return;
    downloadJson('music-projects.json', projects);
  }, [projects]);

  return (
    <div className="space-y-4">
      {/* Create project form */}
      <div className="pac-glass rounded-lg p-4 space-y-3">
        <h2 className="text-sm font-medium text-neutral-300">New Project</h2>

        <form onSubmit={handleCreate} className="space-y-2">
          <input
            type="text"
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            placeholder="Project name"
            className="w-full bg-neutral-900 border border-neutral-700 text-neutral-200
                       rounded px-3 py-1.5 text-sm placeholder:text-neutral-600
                       focus:outline-none focus:border-cyan-600"
            disabled={create.isPending || streaming.active}
          />
          <input
            type="text"
            value={ytUrl}
            onChange={(e) => setYtUrl(e.target.value)}
            placeholder="YouTube URL (optional reference audio)"
            className="w-full bg-neutral-900 border border-neutral-700 text-neutral-200
                       rounded px-3 py-1.5 text-sm placeholder:text-neutral-600
                       focus:outline-none focus:border-cyan-600"
            disabled={create.isPending || streaming.active}
          />
          <button
            type="submit"
            disabled={create.isPending || streaming.active || !projectName.trim()}
            className="px-4 py-1.5 text-sm font-medium rounded bg-cyan-700 text-neutral-100
                       hover:bg-cyan-600 disabled:opacity-40 disabled:cursor-not-allowed
                       transition-colors"
          >
            {create.isPending ? 'Creating…' : 'Create Project'}
          </button>
        </form>

        {create.isError && (
          <p className="text-xs text-red-400">{create.error.message}</p>
        )}

        {streaming.active && streaming.scopeKey && (
          <div className="pac-glass-raised rounded p-3 space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-xs text-neutral-400">
                Creating: <span className="text-cyan-400">{streaming.scopeKey}</span>
              </span>
              <button
                onClick={clearStream}
                className="text-[10px] text-neutral-600 hover:text-neutral-400"
              >
                dismiss
              </button>
            </div>
            {streaming.events.map((ev, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <span className={`w-1.5 h-1.5 rounded-full ${
                  ev.stage === 'failed' ? 'bg-red-500' :
                  ev.stage === 'complete' ? 'bg-green-500' :
                  'bg-cyan-500 animate-pulse'
                }`} />
                <span className="text-neutral-400">{ev.stage}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Save / Import panel */}
      <div className="pac-glass rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-medium text-neutral-300">Save &amp; Import</h2>
          <button
            onClick={exportAll}
            disabled={projects.length === 0}
            className="px-3 py-1 text-xs font-medium rounded border border-neutral-700
                       text-neutral-300 hover:border-cyan-600 hover:text-cyan-300
                       disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Export library
          </button>
        </div>

        <textarea
          value={importText}
          onChange={(e) => setImportText(e.target.value)}
          placeholder='Paste project JSON here (a single object or an array)…'
          rows={3}
          className="w-full bg-neutral-900 border border-neutral-700 text-neutral-200
                     rounded px-3 py-1.5 text-xs font-mono placeholder:text-neutral-600
                     focus:outline-none focus:border-cyan-600 resize-y"
          disabled={importMutation.isPending}
        />

        <div className="flex items-center gap-2">
          <button
            onClick={handleImportText}
            disabled={importMutation.isPending || !importText.trim()}
            className="px-4 py-1.5 text-sm font-medium rounded bg-cyan-700 text-neutral-100
                       hover:bg-cyan-600 disabled:opacity-40 disabled:cursor-not-allowed
                       transition-colors"
          >
            {importMutation.isPending ? 'Importing…' : 'Import JSON'}
          </button>
          <button
            onClick={() => fileRef.current?.click()}
            disabled={importMutation.isPending}
            className="px-4 py-1.5 text-sm font-medium rounded border border-neutral-700
                       text-neutral-300 hover:border-cyan-600 hover:text-cyan-300
                       disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Upload file…
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="application/json,.json"
            onChange={handleFile}
            className="hidden"
          />
        </div>

        {importError && <p className="text-xs text-red-400">{importError}</p>}
        {importMutation.isError && (
          <p className="text-xs text-red-400">{importMutation.error.message}</p>
        )}
        {importMutation.isSuccess && !importMutation.isPending && (
          <p className="text-xs text-green-400">Imported successfully.</p>
        )}
      </div>

      {/* Project library */}
      <div className="pac-glass rounded-lg">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-neutral-800">
          <h2 className="text-sm font-medium text-neutral-300">
            Library <span className="text-neutral-600">({projects.length})</span>
          </h2>
          <div className="flex items-center gap-2">
            <FreshnessBadge state={freshness} />
            <button
              onClick={() => query.refetch()}
              disabled={isFetching}
              className="text-[10px] text-neutral-500 hover:text-neutral-300 disabled:opacity-40"
            >
              refresh
            </button>
          </div>
        </div>

        {isLoading && (
          <div className="p-4 text-sm text-neutral-500 animate-pulse">Loading projects…</div>
        )}
        {isError && !isLoading && (
          <div className="p-4 text-sm text-red-400">Error: {error?.message}</div>
        )}
        {!isLoading && !isError && projects.length === 0 && (
          <div className="p-4 text-sm text-neutral-500">No music projects yet.</div>
        )}
        {projects.map((proj) => (
          <div key={proj.scope_key} className="px-4 py-3 border-b border-neutral-800 last:border-0">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <span className="block text-sm text-neutral-200 truncate">
                  {proj.project_name || proj.scope_key}
                </span>
                {proj.audio_bpm != null && (
                  <span className="text-xs text-neutral-500">{proj.audio_bpm} BPM</span>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                  proj.status === 'completed'
                    ? 'bg-green-900/40 text-green-400'
                    : proj.status === 'failed'
                    ? 'bg-red-900/40 text-red-400'
                    : 'bg-cyan-900/40 text-cyan-400'
                }`}>
                  {proj.status}
                </span>
                <button
                  onClick={() => exportProject(proj)}
                  className="text-[10px] text-neutral-500 hover:text-cyan-300"
                  title="Download project JSON"
                >
                  save
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
